// data/lab/findings-published.js
// ═══════════════════════════════════════════════════════════
// PUBLISHED FINDINGS — Lab platform-level summary
//
// This module is the source-of-truth for findings that have
// passed calibration and are publishable per V2 §6.1. The
// Simulate tab in the Continuum project workspace reads this
// module to render finding cards.
//
// Initially empty — fills in as projects produce calibration-
// passed findings. Pre-calibration findings live in
// data/lab/projects/<id>/findings/ (gitignored) and are NOT
// surfaced here until they pass calibration.
//
// Each finding entry follows the V2 §6.1 schema:
//   {
//     finding_id, card_id, project_id, methodology_version,
//     linked_hypothesis_id, epistemic_status, acquisition_path,
//     calibration_status, publishable, ...
//   }
// ═══════════════════════════════════════════════════════════

export const PUBLISHED_FINDINGS = [
  // ─── Finding 1 — SC-PD-001 effect-size (2026-05-07) ──────
  // First Lab finding to clear the publish gate. AWAITING_ANCHOR
  // path: the absolute level can't be calibrated against Korea TFR
  // (counterfactual-extremes design pins cultural_engagement to
  // 0.10 / 0.70). The pre-registered effect size is the testable
  // claim and it matched.
  {
    finding_id: 'F-SC-PD-001-2026-05-07T21-20-41',
    card_id: 'SC-PD-001',
    project_id: 'population-decline',
    methodology_version: 'V2.0',
    linked_hypothesis_id: 'H-Continuum-11',
    epistemic_status: 'effect_size_pre_registered',
    acquisition_path: 'A_claude_prior',
    calibration_status: 'awaiting_anchor',
    publishable: true,

    headline:
      'Switching the cultural-cooperative trait from extreme-low (10/100) ' +
      'to extreme-high (70/100), holding Seoul-CA demographics constant, ' +
      'shifts TFR-equivalent fertility intent by +0.31 in a 30-year-old ' +
      'female cohort facing a partner-initiated family-formation decision.',

    arm_A: {
      arm_id: 'arm_A',
      arm_label: 'Cultural absent (Korea-as-is extreme)',
      n: 50,
      tfr_equivalent_intent: 0.28,
      decision_mix: { maybe: 0.70, no: 0.30 },
      top_factors: ['peer_norms', 'financial_uncertainty', 'housing_cost'],
    },
    arm_B: {
      arm_id: 'arm_B',
      arm_label: 'Cultural engaged (counterfactual extreme)',
      n: 50,
      tfr_equivalent_intent: 0.59,
      decision_mix: { yes_later: 0.38, maybe: 0.62 },
      top_factors: ['peer_norms', 'housing_cost', 'my_career'],
    },
    differential_B_minus_A: 0.31,
    pre_registered_range: [0.15, 0.35],
    falsifier_threshold: 0.05,
    falsifier_triggered: false,
    verdict: 'PREDICTION_MATCHED',

    publish_caveats: [
      'Counterfactual-extremes design — neither arm represents the population' +
      '-weighted Seoul-CA distribution. The pre-registered effect size IS the ' +
      'testable claim, not absolute fertility level.',
      'Sample size: n=50 per arm (1/30th of the locked card target of 1500 / arm). ' +
      'Slice run for methodology validation. Full $5.28 run will tighten error bars.',
      'Phase 1 / Path A — agent decisions sampled from Claude (Haiku 4.5) prior ' +
      'conditioned on synthesized Seoul-CA demographic + cognitive traits. ' +
      'Path B (LLM-corpus inference) and Path C (survey commissioning) not run.',
      'Calibration awaiting anchor: no published Korean fertility-intent survey ' +
      'is stratified by cultural-cooperative trait extreme-percentile, so the ' +
      'absolute level cannot be validated against an empirical reference yet.',
    ],

    cost_usd: 0.16,
    runtime_seconds: 44,
    timestamp_run: '2026-05-07T21-20-41',
    finding_file: 'data/lab/projects/population-decline/findings/SC-PD-001-2026-05-07T21-20-41.json',
  },
];

// ─── Project status — tracked across all Lab projects ───
// Updated as the build sequence progresses or runs complete.
export const PROJECTS_STATUS = [
  {
    project_id: 'population-decline',
    project_label: 'Population Decline (Continuum first project)',
    methodology_version: 'V2.0',
    status: 'phase_1_engineering',
    cards: [
      {
        card_id: 'SC-PD-001',
        title: 'Korea Cultural-Layer Counterfactual',
        linked_hypothesis: 'H-Continuum-11',
        arms: 2,
        agents_per_arm: 1500,
        total_calls: 3000,
        estimated_cost_usd: 5.28,
        pipeline: {
          card_locked: true,
          agents_generated: true,
          runs_completed: true,    // 50/arm slice run 2026-05-07
          aggregated: true,
          calibrated: true,         // AWAITING_ANCHOR (effect-size path)
          published: true,
        },
        latest_verdict: 'PREDICTION_MATCHED',
        latest_run: {
          n_per_arm: 50,
          differential: 0.31,
          predicted_range: [0.15, 0.35],
          cost_usd: 0.16,
          timestamp: '2026-05-07T21-20-41',
        },
        run_command: 'node scripts/lab/run-stimulus.js SC-PD-001 --limit 50 --yes',
      },
      {
        card_id: 'SC-PD-002',
        title: 'Demographic Refugia Density-Vary',
        linked_hypothesis: 'H-Continuum-16',
        arms: 4,
        agents_per_arm: 2000,
        total_calls: 8000,
        estimated_cost_usd: 14.08,
        pipeline: {
          card_locked: true,
          agents_generated: false,
          runs_completed: false,
          aggregated: false,
          calibrated: false,
          published: false,
        },
        latest_verdict: null,
        run_command: 'node scripts/lab/run-stimulus.js SC-PD-002 --yes',
      },
      {
        card_id: 'SC-PD-003',
        title: 'Cross-Substrate Allee Threshold (KEYSTONE)',
        linked_hypothesis: 'H-Continuum-15',
        arms: 4,
        agents_per_arm: 1500,
        total_calls: 6000,
        estimated_cost_usd: 10.56,
        keystone: true,
        pipeline: {
          card_locked: true,
          agents_generated: false,
          runs_completed: false,
          aggregated: false,
          calibrated: false,
          published: false,
        },
        latest_verdict: null,
        run_command: 'node scripts/lab/run-stimulus.js SC-PD-003 --yes',
      },
      {
        card_id: 'SC-PD-004',
        title: 'Cultural-Regime Hysteresis (Pre/Post)',
        linked_hypothesis: 'H-Continuum-18',
        arms: 2,
        agents_per_arm: 1500,
        total_calls: 3000,
        estimated_cost_usd: 5.28,
        pipeline: {
          card_locked: true,
          agents_generated: false,
          runs_completed: false,
          aggregated: false,
          calibrated: false,
          published: false,
        },
        latest_verdict: null,
        run_command: 'node scripts/lab/run-stimulus.js SC-PD-004 --yes',
      },
      {
        card_id: 'SC-PD-005',
        title: 'X-Factor 2³ Full-Factorial',
        linked_hypothesis: 'project_x_factor_hypothesis',
        arms: 8,
        agents_per_arm: 1000,
        total_calls: 8000,
        estimated_cost_usd: 14.08,
        pipeline: {
          card_locked: true,
          agents_generated: false,
          runs_completed: false,
          aggregated: false,
          calibrated: false,
          published: false,
        },
        latest_verdict: null,
        run_command: 'node scripts/lab/run-stimulus.js SC-PD-005 --yes',
      },
    ],
    total_estimated_cost_usd: 49.28,
    calibration_target: {
      primary: 'Korea TFR 2024 ± 0.10 (actual: 0.72)',
      secondary: 'Israel secular / Italy / Hungary ± 0.10-0.20',
    },
  },
];

// Build-sequence status per V2 spec
export const PHASE_1_BUILD_STATUS = [
  { session: 1, label: 'Project structure + V2 validator + 5 cards locked', status: 'shipped' },
  { session: 2, label: 'Agent generator + stratified L1-L3 sampling', status: 'shipped' },
  { session: 3, label: 'Stimulus encoder + per-agent prompt template', status: 'shipped' },
  { session: 4, label: 'Parallel orchestrator + run-stimulus CLI', status: 'shipped' },
  { session: 5, label: 'Aggregator + verdict-against-falsifier CLI', status: 'shipped' },
  { session: 6, label: 'Simulate tab in Continuum project workspace', status: 'shipped' },
  { session: 7, label: 'Calibration vs Korea-Estonia empirical anchor', status: 'shipped' },
];
